import { useFormik } from "formik";
import { UserFormProps, UserFormValues } from "./user-form.type";
import * as yup from "yup";
import React from "react";
import { LayoutForm } from "lib/components/layout/form";
import { useCreateUserMutation, useUpdateUserMutation } from "api/endpoints";
import { toast } from "react-toastify";
import { ROUTE_PATH } from "router/router-constant/router.constant";
import { useNavigate } from "react-router-dom";
import { AxiosErrorData } from "api/api.type";

// import { DepartmentsAcl } from "store/reducers/acl/acl.type";
import { TextField } from "@mui/material";
import { NumberInput, EnhancedFormField } from "lib/components/ui";
import { SelectField } from "lib/components/ui/select-field";

export const UserForm = ({ initValues }: UserFormProps) => {
  const validationSchema = yup.object({
    fullName: yup.string().required("وارد نمودن نام الزامی است"),
    role: yup.string().required("انتخاب نمودن سمت الزامی است"),
    phoneNumber: yup.string().required("وارد نمودن شماره موبایل الزامی است"),
    password: yup.string().when([], {
      is: () => !initValues?._id, // Only require password for new users
      then: (schema) =>
        schema
          .required("وارد نمودن رمز عبور الزامی است")
          .min(6, "رمز عبور باید حداقل ۶ کاراکتر باشد"),
      otherwise: (schema) => schema.optional(),
    }),
  });
  const navigate = useNavigate();
  const [createUserMutation] = useCreateUserMutation();
  const [updateUserMutation] = useUpdateUserMutation();

  const ROLES = [
    { label: "کاربر", value: "user" },
    { label: "مدیر", value: "admin" },
  ];

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldError,
    isSubmitting,
  } = useFormik<UserFormValues>({
    initialValues: {
      fullName: initValues?.fullName || "",
      phoneNumber: initValues?.phoneNumber || "",
      password: "", // Password is always empty initially
      role: initValues?.role || "user",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (initValues?._id) {
        // For updates, only include password if it's provided and not empty
        const updatePayload = { ...values };
        if (!updatePayload.password || updatePayload.password.trim() === "") {
          delete updatePayload.password;
        }

        await toast.promise(
          updateUserMutation({
            params: {
              userId: initValues?._id,
            },
            payload: updatePayload,
          }).unwrap(),
          {
            error: {
              render: ({ data }: { data: AxiosErrorData }) => {
                const error = data.response?.data;
                if (error?.statusCode === 409) {
                  setFieldError("phoneNumber", "شماره موبایل تکراری است.");
                  return "شماره موبایل از قبل در سیستم ایجاد شده است.";
                } else {
                  return "هنگام ویرایش کاربر مشکلی بوجود آمد، لطفا با پشتیبانی ارتباط برقرار نمایید.";
                }
              },
              // autoClose: 5000,
            },
            pending: {
              render: () => "درحال ویرایش کاربر، لطفا منتظر بمانید...",
              autoClose: 5000,
            },
            success: "کاربر با موفقیت ویرایش شد.",
          }
        );
      } else {
        await toast.promise(
          createUserMutation({
            payload: {
              ...values,
              password: values.password || values.phoneNumber.slice(-4),
            },
          }).unwrap(),
          {
            error: {
              render: ({ data }: { data: AxiosErrorData }) => {
                const error = data.response?.data;
                if (error?.statusCode === 409) {
                  setFieldError("phoneNumber", "شماره موبایل تکراری است.");
                  return "شماره موبایل از قبل در سیستم ایجاد شده است.";
                } else {
                  return "هنگام ایجاد کاربر مشکلی بوجود آمد، لطفا با پشتیبانی ارتباط برقرار نمایید.";
                }
              },
              // autoClose: 5000,
            },
            pending: {
              render: () => "درحال ایجاد کاربر، لطفا منتظر بمانید...",
              autoClose: 5000,
            },
            success: "کاربر با موفقیت ایجاد شد.",
          }
        );
      }

      navigate(ROUTE_PATH.users.list.path);
    },
  });

  const formTitle = initValues ? "ویرایش کاربر" : "ایجاد کاربر";

  const allowToAction = true; // For now, allow all actions

  return allowToAction ? (
    <LayoutForm
      backUrl={ROUTE_PATH.users.list.path}
      disabledSubmit={isSubmitting}
      handleSubmit={handleSubmit}
      formTitle={formTitle}
    >
      <EnhancedFormField
        label="نام"
        value={values.fullName}
        name="fullName"
        dir="rtl"
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.fullName && Boolean(errors.fullName)}
        helperText={touched.fullName ? errors.fullName : undefined}
        mobileOptimized={true}
      />
      <EnhancedFormField
        label="شماره موبایل"
        value={values.phoneNumber}
        name="phoneNumber"
        dir="rtl"
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.phoneNumber && Boolean(errors.phoneNumber)}
        helperText={touched.phoneNumber ? errors.phoneNumber : undefined}
        mobileOptimized={true}
      />
      <EnhancedFormField
        label={initValues?._id ? "رمز عبور جدید (اختیاری)" : "رمز عبور"}
        type="password"
        value={values.password}
        name="password"
        dir="rtl"
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.password && Boolean(errors.password)}
        helperText={touched.password ? errors.password : undefined}
        placeholder={
          initValues?._id
            ? "برای تغییر رمز عبور، رمز جدید وارد کنید"
            : "رمز عبور را وارد کنید"
        }
        mobileOptimized={true}
      />
      <SelectField
        label="سمت"
        value={values.role}
        name="role"
        onChange={handleChange}
        options={ROLES}
      />
    </LayoutForm>
  ) : (
    <></>
  );
};
